import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Download, Mail, Globe, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const InvoicePdfPreviewDialog = ({ open, onOpenChange, type = 'invoice', invoiceId, statementData, onDelete }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const title = type === 'statement'
    ? `Statement - ${statementData?.crm_entity_name || ''}`
    : `Invoice Preview`;

  const loadPdf = async () => {
    setLoading(true);
    setPdfUrl(null);
    try {
      let res;
      if (type === 'statement' && statementData) {
        res = await api.post('/invoices/generate-statement-pdf', {
          crm_entity_id: statementData.crm_entity_id,
          invoice_ids: statementData.invoice_ids || [],
        }, { responseType: 'blob' });
      } else if (invoiceId) {
        res = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      }
      if (res) {
        const blob = new Blob([res.data], { type: 'application/pdf' });
        setPdfUrl(window.URL.createObjectURL(blob));
      }
    } catch { toast.error('Failed to load PDF'); }
    setLoading(false);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      let res, filename;
      if (type === 'statement' && statementData) {
        res = await api.post('/invoices/generate-statement-pdf', {
          crm_entity_id: statementData.crm_entity_id,
          invoice_ids: statementData.invoice_ids || [],
        }, { responseType: 'blob' });
        filename = `Statement-${statementData.crm_entity_name || 'owner'}.pdf`;
      } else if (invoiceId) {
        res = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
        filename = `Invoice-${invoiceId.slice(0, 8)}.pdf`;
      }
      if (res) {
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('PDF downloaded');
      }
    } catch { toast.error('Failed to download'); }
    setDownloading(false);
  };

  // Load PDF when dialog opens
  React.useEffect(() => {
    if (open) loadPdf();
    else if (pdfUrl) { window.URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 animate-spin" /></div>
          ) : pdfUrl ? (
            <iframe src={pdfUrl} className="w-full h-full border rounded" title="PDF Preview" />
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">Failed to load preview</div>
          )}
        </div>
        <div className="flex justify-between items-center pt-2 border-t">
          <div>
            {onDelete && (
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-red-600" onClick={() => { onDelete(); onOpenChange(false); }} data-testid="pdf-preview-delete-btn">
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleDownload} disabled={downloading} className="bg-primary hover:bg-primary/90 text-white" data-testid="pdf-preview-download-btn">
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Download PDF
            </Button>
            <Button size="sm" variant="outline" onClick={() => toast.info('Email feature coming soon')} data-testid="pdf-preview-email-btn">
              <Mail className="w-4 h-4 mr-2" /> Email
            </Button>
            <Button size="sm" variant="outline" onClick={() => toast.info('Portal publishing coming soon')} data-testid="pdf-preview-publish-btn">
              <Globe className="w-4 h-4 mr-2" /> Publish to Owner Portal
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InvoicePdfPreviewDialog;
